import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { NewsletterBlock } from "@/components/Layout";
import { PostHeader, VideoEmbed, AISummary, PostBody, ReadingProgress } from "@/pages/blog-post/parts";
import Seo, { SITE } from "@/components/Seo";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summarizing, setSummarizing] = useState(false);

  useEffect(() => {
    setPost(null);
    setSummary(null);
    api.get(`/posts/${slug}`).then((r) => {
      setPost(r.data);
      if (r.data.ai_summary) setSummary(r.data.ai_summary);
    }).catch(() => setNotFound(true));
  }, [slug]);

  const summarize = async () => {
    setSummarizing(true);
    try {
      const r = await api.post(`/posts/${slug}/summarize`);
      setSummary(r.data.summary);
    } catch (e) {
      setSummary("Couldn't generate a summary right now. Please try again.");
    } finally {
      setSummarizing(false);
    }
  };

  if (notFound) {
    return (
      <div className="pt-40 pb-40 text-center">
        <h1 className="font-display text-6xl tracking-tight">Post not found</h1>
        <Link to="/blog" className="inline-block mt-6 text-sm font-bold uppercase tracking-[0.12em] text-[var(--brand-primary)]">← Back to insights</Link>
      </div>
    );
  }

  if (!post) {
    return <div className="pt-40 pb-40 text-center text-[var(--brand-muted)] font-bold uppercase tracking-wide">Loading…</div>;
  }

  return (
    <div className="pt-32">
      <ReadingProgress />
      <Seo
        title={`${post.title} | Kim Kornachuk`}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.cover_image}
        type="article"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.excerpt,
            image: post.cover_image,
            datePublished: post.created_at,
            dateModified: post.updated_at,
            author: { "@type": "Person", name: post.author_name || "Kim Kornachuk", url: SITE },
            mainEntityOfPage: `${SITE}/blog/${post.slug}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE },
              { "@type": "ListItem", position: 2, name: "AI Journey", item: `${SITE}/blog` },
              { "@type": "ListItem", position: 3, name: post.title, item: `${SITE}/blog/${post.slug}` },
            ],
          },
        ]}
      />
      <PostHeader post={post} />

      <div className="max-w-5xl mx-auto px-6 sm:px-10 my-12">
        <img src={post.cover_image} alt={post.title} fetchPriority="high" decoding="async" className="w-full h-auto rounded-[2rem] shadow-[0_24px_50px_-20px_rgba(28,24,21,0.4)]" />
      </div>

      <VideoEmbed url={post.video_url} />

      <article className="max-w-3xl mx-auto px-6 sm:px-10 pb-16">
        <AISummary summary={summary} summarizing={summarizing} onSummarize={summarize} />
        <PostBody post={post} />
      </article>

      <NewsletterBlock />
    </div>
  );
}
